import React, { useState, useEffect, useCallback } from 'react';
import './BestSellers.mgmt.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const BestSellersMgmt = () => {
  const [bestsellers, setBestsellers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addingBrand, setAddingBrand] = useState(null);
  const token = localStorage.getItem('token');

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const base = API_URL.replace('/api', '');
    if (path.startsWith('/uploads/')) return `${base}${path}`;
    return `${base}/api/images/${path}`;
  };

  const fetchBestsellers = useCallback(() => {
    fetch(`${API_URL}/bestsellers`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.success) setBestsellers(d.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const fetchBrands = useCallback(() => {
    fetch(`${API_URL}/brands`)
      .then(r => r.json())
      .then(d => { if (d.success) setBrands((d.data || []).filter(b => b.product_count > 0)); })
      .catch(console.error);
  }, []);

  useEffect(() => { fetchBestsellers(); fetchBrands(); }, [fetchBestsellers, fetchBrands]);

  // Search products
  const doSearch = async (q) => {
    setSearch(q);
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/bestsellers/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        const existingIds = new Set(bestsellers.map(b => b.product_group_id));
        setSearchResults((data.data || []).filter(p => !existingIds.has(p.group_id)));
      }
    } catch (err) { console.error(err); }
    finally { setSearching(false); }
  };

  const addProduct = async (groupId) => {
    try {
      await fetch(`${API_URL}/bestsellers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ product_group_id: groupId }),
      });
      fetchBestsellers();
      // Remove from search results
      setSearchResults(prev => prev.filter(p => p.group_id !== groupId));
    } catch (err) { console.error(err); }
  };

  const removeProduct = async (groupId) => {
    try {
      await fetch(`${API_URL}/bestsellers/${groupId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setBestsellers(prev => prev.filter(b => b.product_group_id !== groupId));
    } catch (err) { console.error(err); }
  };

  const addBrand = async (vendorName) => {
    setAddingBrand(vendorName);
    try {
      const res = await fetch(`${API_URL}/bestsellers/brand`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ vendor_name: vendorName }),
      });
      const data = await res.json();
      if (data.success) fetchBestsellers();
    } catch (err) { console.error(err); }
    finally { setAddingBrand(null); }
  };

  const removeBrand = async (vendorName) => {
    setAddingBrand(vendorName);
    try {
      await fetch(`${API_URL}/bestsellers/brand/${encodeURIComponent(vendorName)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchBestsellers();
    } catch (err) { console.error(err); }
    finally { setAddingBrand(null); }
  };

  // Check which brands have all their products in bestsellers
  const brandInBestsellers = (brandName) => {
    return bestsellers.some(b => b.vendor_name === brandName);
  };

  const countBrandInBestsellers = (brandName) => {
    return bestsellers.filter(b => b.vendor_name === brandName).length;
  };

  if (loading) return <div className="bsmgmt-loading">Loading...</div>;

  return (
    <div className="bsmgmt-page">
      <div className="bsmgmt-header">
        <div>
          <h2>Best Sellers</h2>
          <p className="bsmgmt-subtitle">{bestsellers.length} product{bestsellers.length !== 1 ? 's' : ''} selected · Shown at /best-sellers</p>
        </div>
      </div>

      <div className="bsmgmt-layout">
        {/* Left: Add products */}
        <div className="bsmgmt-add-panel">
          {/* Add by Brand */}
          <div className="bsmgmt-card">
            <h3>Add by Brand</h3>
            <p className="bsmgmt-desc">Add all products from a brand at once.</p>
            <div className="bsmgmt-brand-list">
              {brands.map(brand => {
                const count = countBrandInBestsellers(brand.name);
                const isIn = brandInBestsellers(brand.name);
                return (
                  <div key={brand.id} className="bsmgmt-brand-row">
                    <div className="bsmgmt-brand-info">
                      <span className="bsmgmt-brand-name">{brand.name}</span>
                      <span className="bsmgmt-brand-count">{brand.product_count} products{count > 0 ? ` · ${count} in best sellers` : ''}</span>
                    </div>
                    <div className="bsmgmt-brand-actions">
                      <button
                        className="btn-add-brand"
                        onClick={() => addBrand(brand.name)}
                        disabled={addingBrand === brand.name}
                      >
                        {addingBrand === brand.name ? '...' : '+ Add All'}
                      </button>
                      {isIn && (
                        <button
                          className="btn-remove-brand"
                          onClick={() => removeBrand(brand.name)}
                          disabled={addingBrand === brand.name}
                        >
                          Remove All
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Add Individual Products */}
          <div className="bsmgmt-card">
            <h3>Add Individual Products</h3>
            <p className="bsmgmt-desc">Search by product name or brand.</p>
            <div className="bsmgmt-search-wrap">
              <input
                className="bsmgmt-search"
                placeholder="Type to search products..."
                value={search}
                onChange={e => doSearch(e.target.value)}
              />
              {searching && <span className="bsmgmt-searching">Searching...</span>}
            </div>
            {searchResults.length > 0 && (
              <div className="bsmgmt-search-results">
                {searchResults.map(p => (
                  <div key={p.group_id} className="bsmgmt-search-item">
                    <div className="bsmgmt-item-thumb">
                      {p.hero_image ? <img src={getImageUrl(p.hero_image)} alt="" /> : <span>—</span>}
                    </div>
                    <div className="bsmgmt-item-info">
                      <span className="bsmgmt-item-title">{p.title}</span>
                      <span className="bsmgmt-item-meta">{p.vendor_name} · ${parseFloat(p.min_price).toFixed(2)}{p.total_variants > 1 ? ` · ${p.total_variants} variants` : ''}</span>
                    </div>
                    <button className="btn-add-product" onClick={() => addProduct(p.group_id)}>+ Add</button>
                  </div>
                ))}
              </div>
            )}
            {search.length >= 2 && !searching && searchResults.length === 0 && (
              <p className="bsmgmt-no-results">No products found or all matches already added.</p>
            )}
          </div>
        </div>

        {/* Right: Current bestsellers list */}
        <div className="bsmgmt-list-panel">
          <div className="bsmgmt-card">
            <h3>Current Best Sellers ({bestsellers.length})</h3>
            {bestsellers.length === 0 ? (
              <p className="bsmgmt-empty">No products added yet. Use the panel on the left to add brands or individual products.</p>
            ) : (
              <div className="bsmgmt-current-list">
                {bestsellers.map((p, i) => (
                  <div key={p.product_group_id} className="bsmgmt-list-item">
                    <span className="bsmgmt-list-num">{i + 1}</span>
                    <div className="bsmgmt-item-thumb">
                      {p.hero_image ? <img src={getImageUrl(p.hero_image)} alt="" /> : <span>—</span>}
                    </div>
                    <div className="bsmgmt-item-info">
                      <span className="bsmgmt-item-title">{p.title}</span>
                      <span className="bsmgmt-item-meta">{p.vendor_name}</span>
                    </div>
                    <button className="btn-remove-product" onClick={() => removeProduct(p.product_group_id)}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BestSellersMgmt;
